import { CheckCircleIcon } from '@repo/studio-design-system-ui-icons/general/check-circle'
import { useUserSubscriptionIdQuery } from '@repo/cf-user/hooks/use-user-data'
import { usePaymentsChangeSubscriptionPlanMutation } from '@repo/studio-hooks-coins/gen/coins'
import { toast } from '@repo/studio-components-toast'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import type { CoinSubscriptionPlan, DefaultPlan } from 'payments/payments-dialog/lib/plans'
import {
  subscriptionRequiresPaymentRecovery,
  tryOpenPaymentRequiredDialogFromSubscription,
} from '@repo/studio-hooks-payments/use-payment-required-dialog'
import {
  COIN_PLAN_TO_SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_TO_COIN_PLAN,
  getTierForCoinPlan,
} from '@repo/studio-hooks-coins/constants/coin-top-up'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

export function useSwapCoinPlan() {
  const { data: subscriptionId } = useUserSubscriptionIdQuery('coins')
  const t = useStudioTranslate()
  const swapMutation = usePaymentsChangeSubscriptionPlanMutation()

  const swapPlan = swapMutation.variables?.input.newPlan
  const swapSubscription = swapMutation.data?.paymentsChangeSubscriptionPlan.subscription
  // A `requires_action` / failed invoice still resolves the mutation without
  // GraphQL errors; gate `successPlan` on actual payment success so the
  // top-up dialog stays open behind the recovery dialog stacked on top.
  const succeeded =
    swapMutation.isSuccess &&
    !swapMutation.data?.paymentsChangeSubscriptionPlan.errors?.length &&
    !subscriptionRequiresPaymentRecovery(swapSubscription)

  const successPlan: DefaultPlan | null =
    succeeded && swapPlan ? (SUBSCRIPTION_PLAN_TO_COIN_PLAN[swapPlan] ?? null) : null

  function swapToPlan(plan: CoinSubscriptionPlan) {
    if (!subscriptionId) {
      return
    }

    swapMutation.mutate(
      {
        input: {
          subscriptionId,
          newPlan: COIN_PLAN_TO_SUBSCRIPTION_PLAN[plan],
        },
      },
      {
        onSuccess(data) {
          const { errors, subscription } = data.paymentsChangeSubscriptionPlan
          if (errors?.length) {
            toast({ title: t('studio.coins.top_up_dialog.swap_error'), description: '' })
            return
          }
          if (tryOpenPaymentRequiredDialogFromSubscription(subscription)) {
            return
          }
          const tier = getTierForCoinPlan(plan)
          if (tier) {
            sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
              coin_pack_type: 'subscription',
              coin_pack: `${tier.coins} coins`,
              price: tier.priceUsd,
            })
          }
          toast({
            title: t('studio.coins.top_up_dialog.swap_success'),
            description: '',
            icon: <CheckCircleIcon />,
          })
        },
        onError() {
          toast({ title: t('studio.coins.top_up_dialog.swap_error'), description: '' })
        },
      },
    )
  }

  return { swapToPlan, successPlan, reset: swapMutation.reset }
}
